import { assertStorageBudget } from "./memory-safety";
import { loadPrefs } from "./prefs";

export type StoredSession = {
  id: "active" | string;
  fileName: string;
  fileBuffer: ArrayBuffer;
  pageCount: number;
  layers: unknown[];
  updatedAt: number;
};

export type SavedSignature = {
  id: string;
  name: string;
  dataUrl: string;
  updatedAt: number;
};

const DB_NAME = "localpdf-studio";
const DB_VERSION = 2;
const SESSION_STORE = "sessions";
const SIGNATURE_STORE = "signatures";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SESSION_STORE)) {
        db.createObjectStore(SESSION_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(SIGNATURE_STORE)) {
        db.createObjectStore(SIGNATURE_STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB unavailable"));
  });
}

async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = action(database.transaction(storeName, mode).objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB operation failed"));
  });
}

export async function saveActiveSession(session: Omit<StoredSession, "id" | "updatedAt">) {
  if (!loadPrefs().rememberSession) return;
  await assertStorageBudget(session.fileBuffer.byteLength + JSON.stringify(session.layers).length * 2);
  const record: StoredSession = { ...session, id: "active", updatedAt: Date.now() };
  try {
    await withStore(SESSION_STORE, "readwrite", (store) => store.put(record));
    await withStore(SESSION_STORE, "readwrite", (store) =>
      store.put({ ...record, id: `recent:${session.fileName}` }),
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      throw new Error("Browser storage quota was reached. Export the PDF and clear old local sessions.");
    }
    throw error;
  }
}

export async function loadActiveSession(): Promise<StoredSession | undefined> {
  return withStore(SESSION_STORE, "readonly", (store) => store.get("active"));
}

export async function loadSession(id: string): Promise<StoredSession | undefined> {
  return withStore(SESSION_STORE, "readonly", (store) => store.get(id));
}

export async function loadRecentSessions(): Promise<StoredSession[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(SESSION_STORE, "readonly").objectStore(SESSION_STORE).getAll();
    request.onsuccess = () =>
      resolve(
        (request.result as StoredSession[])
          .filter((item) => String(item.id).startsWith("recent:"))
          .sort((a, b) => b.updatedAt - a.updatedAt)
          .slice(0, 8),
      );
    request.onerror = () => reject(request.error ?? new Error("IndexedDB operation failed"));
  });
}

export async function clearActiveSession() {
  await withStore(SESSION_STORE, "readwrite", (store) => store.delete("active"));
}

export async function countLocalRecords(): Promise<{ sessions: number; signatures: number }> {
  const sessions = await withStore<number>(SESSION_STORE, "readonly", (store) => store.count());
  const signatures = await withStore<number>(SIGNATURE_STORE, "readonly", (store) => store.count());
  return { sessions, signatures };
}

export async function loadSavedSignatures(): Promise<SavedSignature[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(SIGNATURE_STORE, "readonly").objectStore(SIGNATURE_STORE).getAll();
    request.onsuccess = () =>
      resolve(
        ((request.result as SavedSignature[]) ?? []).sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 8),
      );
    request.onerror = () => reject(request.error ?? new Error("IndexedDB operation failed"));
  });
}

export async function saveSignature(
  signature: Omit<SavedSignature, "id" | "updatedAt">,
): Promise<SavedSignature> {
  const next: SavedSignature = {
    ...signature,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    updatedAt: Date.now(),
  };
  const existing = await loadSavedSignatures();
  await withStore(SIGNATURE_STORE, "readwrite", (store) => store.put(next));
  for (const stale of existing.slice(7)) {
    await withStore(SIGNATURE_STORE, "readwrite", (store) => store.delete(stale.id));
  }
  return next;
}

export async function clearAllLocalData() {
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("Could not clear IndexedDB"));
    request.onblocked = () => resolve();
  });
  localStorage.removeItem("localpdf-saved-signatures");
  localStorage.removeItem("localpdf-prefs");
}
